import { memo, useEffect, useMemo, useState } from "react";

// material-ui
import { Typography } from "@mui/material";

// project imports
import NavGroup from "./NavGroup";
import menuItem from "menu-items";
import useAuth from "hooks/useAuth";
import axios from "utils/axios";
import useContactInquirySummary from "hooks/useContactInquirySummary";
import usePaymentTransferSummary from "hooks/usePaymentTransferSummary";

// Live badges for menu items (e.g. number of new contact inquiries).
const withBadges = (item, badges) => {
  if (item.children) {
    return { ...item, children: item.children.map((child) => withBadges(child, badges)) };
  }
  const count = badges[item.id];
  if (!count) return item;
  return {
    ...item,
    chip: { label: count > 99 ? "99+" : String(count), color: "error", size: "small" },
  };
};

const filterByRole = (item, role, permissionMap = {}) => {
  if (item.roles && !item.roles.includes(role)) {
    return null;
  }

  if (permissionMap[item.id] === false) {
    return null;
  }

  if (item.children) {
    const children = item.children
      .map((child) => filterByRole(child, role, permissionMap))
      .filter(Boolean);

    if (!children.length) {
      return null;
    }

    return { ...item, children };
  }

  return item;
};

// ==============================|| SIDEBAR MENU LIST ||============================== //

const MenuList = () => {
  const { user } = useAuth();
  const role = user?.role || "admin";
  const [permissionMap, setPermissionMap] = useState({});
  const { counts: inquiryCounts } = useContactInquirySummary();
  const newInquiries = inquiryCounts.new || 0;
  const { pendingReview } = usePaymentTransferSummary();

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const response = await axios.get("/system/menu-permissions", {
          params: { role },
        });
        setPermissionMap(response.data?.data?.permissions || {});
      } catch (_err) {
        setPermissionMap({});
      }
    };

    loadPermissions();
  }, [role]);

  const allowedItems = useMemo(
    () =>
      menuItem.items
        .map((item) => filterByRole(item, role, permissionMap))
        .filter(Boolean)
        .map((item) =>
          withBadges(item, {
            "contact-inquiries": newInquiries,
            "payment-transfers": pendingReview,
          }),
        ),
    [role, permissionMap, newInquiries, pendingReview],
  );

  const navItems = allowedItems.map((item) => {
    switch (item.type) {
      case "group":
        return <NavGroup key={item.id} item={item} />;
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  });

  return <>{navItems}</>;
};

export default memo(MenuList);
